import { useEffect, useState } from 'react';
import type { FleetClient, FleetCommandClient, EnqueueFleetCommand } from '@galaxis/shared';
import { useGameStore } from '../../store/game-store.ts';
import {
  fetchFleetCommands, enqueueFleetCommand, cancelFleetCommand, reorderFleetCommands, stopFleetCommands,
} from '../../api/galaxy.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { formatDuration, newIdempotencyKey } from './fleet-format.ts';

const TYPE_LABELS: Record<FleetCommandClient['type'], string> = {
  move: 'Mozgatás',
  survey: 'Felmérés',
  hold: 'Várakozás',
  patrol: 'Járőrözés',
  auto_survey: 'Automatikus felfedezés',
};

const STATUS_LABELS: Record<FleetCommandClient['status'], string> = {
  queued: 'Sorban', active: 'Aktív', completed: 'Kész', cancelled: 'Törölve', failed: 'Sikertelen',
};

const REASON_LABELS: Record<string, string> = {
  unreachable: 'nem érhető el',
  route_unreachable: 'az útvonal elérhetetlenné vált',
  route_blocked: 'ellenséges rendszer az útvonalon',
  no_scout: 'kutatóhajó kell',
  no_targets: 'nincs több felméretlen rendszer',
  fleet_inactive: 'a flotta megszűnt',
  manual_override: 'kézi parancs felülírta',
  cancelled_by_player: 'törölve',
  stopped_by_player: 'leállítva',
};

const HOLD_OPTIONS = [30, 60, 300, 900];

type Sheet = null | 'add' | 'patrol' | 'stop';
/** Distributive Omit: keeps each command variant's own fields. */
type CommandInput = EnqueueFleetCommand extends infer T ? (T extends unknown ? Omit<T, 'idempotencyKey'> : never) : never;
type AddTab = 'move' | 'survey' | 'hold';

const BTN = 'rounded-lg text-sm font-medium px-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const btn = (color = '#00e5ff') => ({ minHeight: 44, background: `${color}14`, color, border: `1px solid ${color}55` });

export function FleetCommandQueue({ fleet, now, patrolRequest = 0 }: { fleet: FleetClient; now: number; patrolRequest?: number }) {
  const { galaxyMap, setPlannedRoute } = useGameStore();
  const [commands, setCommands] = useState<FleetCommandClient[]>([]);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [addTab, setAddTab] = useState<AddTab>('move');
  const [route, setRoute] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const systemName = (id: string | null | undefined) => (id ? galaxyMap?.systems.find(s => s.id === id)?.name : null) ?? '—';
  const apply = (res: { commands: FleetCommandClient[]; plannedPath: string[] }) => {
    setCommands(res.commands);
    setPlannedRoute({ fleetId: fleet.id, path: res.plannedPath });
  };

  // Fleet UX: the detail view's "Járőrözés" action opens the patrol route sheet
  useEffect(() => {
    if (patrolRequest > 0) { setRoute([]); setSearch(''); setSheet('patrol'); }
  }, [patrolRequest]);

  // Refresh whenever the fleet's state or command summary changes (WS updates refresh `fleet`)
  const refreshKey = `${fleet.version}|${fleet.status}|${fleet.commands.active?.id ?? ''}|${fleet.commands.queued}|${fleet.commands.active?.routeIndex ?? ''}`;
  useEffect(() => {
    let cancelled = false;
    fetchFleetCommands(fleet.id).then(res => { if (!cancelled) apply(res); }).catch(() => undefined);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleet.id, refreshKey]);

  async function run(action: () => Promise<{ commands: FleetCommandClient[]; plannedPath: string[] }>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      apply(await action());
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ismeretlen hiba');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const enqueue = (cmd: CommandInput) =>
    run(() => enqueueFleetCommand(fleet.id, { ...cmd, idempotencyKey: newIdempotencyKey(cmd.type) } as EnqueueFleetCommand));

  const active = commands.find(c => c.status === 'active') ?? null;
  const queued = commands.filter(c => c.status === 'queued');
  const history = commands.filter(c => c.status !== 'active' && c.status !== 'queued');
  const autoCmd = [active, ...queued].find(c => c?.type === 'auto_survey') ?? null;
  const automation = [active, ...queued].find(c => c?.type === 'auto_survey' || c?.type === 'patrol') ?? null;
  const remaining = fleet.eta ? Math.max(0, new Date(fleet.eta).getTime() - now) : null;

  async function move(index: number, delta: -1 | 1) {
    const ids = queued.map(c => c.id);
    const j = index + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j]!, ids[index]!];
    await run(() => reorderFleetCommands(fleet.id, ids));
  }

  function describe(c: FleetCommandClient): string {
    switch (c.type) {
      case 'move': return `${TYPE_LABELS.move} → ${systemName(c.targetSystemId)}`;
      case 'survey': return `${TYPE_LABELS.survey}: ${systemName(c.targetSystemId)}`;
      case 'hold': return `${TYPE_LABELS.hold} ${formatDuration((c.durationSeconds ?? 0) * 1000)}`;
      case 'patrol': return `${TYPE_LABELS.patrol}: ${(c.route ?? []).map(systemName).join(' → ')}`;
      case 'auto_survey': return TYPE_LABELS.auto_survey;
    }
  }

  const known = (galaxyMap?.systems ?? [])
    .filter(s => s.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));

  function systemList(onPick: (id: string) => void, filter?: (s: (typeof known)[number]) => boolean) {
    const list = known.filter(s => !filter || filter(s));
    return (
      <div className="space-y-2">
        <input
          aria-label="Rendszer keresése"
          placeholder="Rendszer keresése…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 text-slate-100 text-sm"
          style={{ minHeight: 44 }}
        />
        <ul className="max-h-64 overflow-y-auto space-y-1.5" aria-label="Ismert rendszerek">
          {list.map(s => (
            <li key={s.id}>
              <button type="button" className={`${BTN} w-full flex justify-between items-center text-left`} style={btn()} disabled={busy} onClick={() => onPick(s.id)}>
                <span>{s.name}</span>
                <span className="text-[11px] text-slate-400">
                  {s.owner ? s.owner.civilizationName : s.discoveryStatus === 'detected' ? 'Meglátogatva' : 'Felmért'}
                </span>
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="text-xs text-slate-500">Nincs találat.</li>}
        </ul>
      </div>
    );
  }

  return (
    <section className="sci-fi-panel rounded-xl p-4 space-y-3" aria-label="Parancssor" data-testid="command-queue">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs text-slate-500 uppercase tracking-wide">Parancssor</h3>
        {automation && (
          <span className="text-[11px] px-2 py-0.5 rounded-full" data-testid="automation-badge"
            style={{ color: '#a78bfa', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.4)' }}>
            {automation.type === 'patrol' ? '🔁 Járőrözik' : '🤖 Automatikus felfedezés'}
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-300" role="alert">{error}</p>}

      {/* Active */}
      <div className="rounded-lg bg-slate-900/60 px-3 py-2 space-y-1" data-testid="active-command">
        {active ? (
          <>
            <div className="text-sm text-slate-100">{describe(active)}</div>
            <div className="text-xs text-slate-400 flex flex-wrap gap-x-3">
              {fleet.destinationSystemId && <span>Következő: {systemName(fleet.destinationSystemId)}</span>}
              {remaining !== null && <span>ETA: <span className="font-mono">{formatDuration(remaining)}</span></span>}
              {active.type === 'patrol' && <span>{(active.laps ?? 0) + 1}. kör</span>}
              {active.type === 'auto_survey' && <span>Felmérve: {active.surveyedCount ?? 0}</span>}
              {active.type === 'hold' && active.holdUntil && (
                <span>Hátra: <span className="font-mono">{formatDuration(new Date(active.holdUntil).getTime() - now)}</span></span>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-400">Nincs aktív parancs</div>
        )}
      </div>

      {/* Queue */}
      {queued.length > 0 && (
        <ol className="space-y-1.5" aria-label="Várakozó parancsok">
          {queued.map((c, i) => (
            <li key={c.id} data-testid="queued-command" className="flex items-center gap-2 bg-slate-900/40 rounded-lg pl-3">
              <span className="flex-1 min-w-0 text-sm text-slate-300 truncate">{i + 1}. {describe(c)}</span>
              <button type="button" aria-label="Feljebb" className="text-slate-300 disabled:opacity-30" style={{ minWidth: 44, minHeight: 44 }} disabled={busy || i === 0} onClick={() => void move(i, -1)}>↑</button>
              <button type="button" aria-label="Lejjebb" className="text-slate-300 disabled:opacity-30" style={{ minWidth: 44, minHeight: 44 }} disabled={busy || i === queued.length - 1} onClick={() => void move(i, 1)}>↓</button>
              <button type="button" aria-label="Törlés" className="text-red-300 disabled:opacity-30" style={{ minWidth: 44, minHeight: 44 }} disabled={busy} onClick={() => void run(() => cancelFleetCommand(fleet.id, c.id))}>✕</button>
            </li>
          ))}
        </ol>
      )}

      {/* Controls */}
      <label className="flex items-center justify-between gap-3 rounded-lg bg-slate-900/40 px-3" style={{ minHeight: 52 }}>
        <span className="text-sm text-slate-200">
          Automatikus felfedezés
          {!fleet.capabilities.survey && <span className="block text-[11px] text-slate-500">Kutatóhajó kell hozzá</span>}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={!!autoCmd}
          aria-label="Automatikus felfedezés"
          disabled={busy || !fleet.capabilities.survey}
          onClick={() => void (autoCmd ? run(() => cancelFleetCommand(fleet.id, autoCmd.id)) : enqueue({ type: 'auto_survey' }))}
          className="flex items-center justify-center disabled:opacity-40"
          style={{ minWidth: 60, minHeight: 44 }}
        >
          <span className="relative rounded-full transition-colors" style={{ width: 52, height: 30, background: autoCmd ? '#a78bfa' : '#334155' }}>
            <span className="absolute top-1 rounded-full bg-white transition-all" style={{ width: 22, height: 22, left: autoCmd ? 26 : 4 }} />
          </span>
        </button>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={BTN} style={btn()} disabled={busy} onClick={() => { setSearch(''); setSheet('add'); }}>＋ Parancs</button>
        <button type="button" className={BTN} style={btn('#a78bfa')} disabled={busy} onClick={() => { setRoute([]); setSearch(''); setSheet('patrol'); }}>🔁 Járőrútvonal létrehozása</button>
      </div>
      {(active || queued.length > 0) && (
        <button type="button" className={`${BTN} w-full`} style={btn('#f87171')} disabled={busy} onClick={() => setSheet('stop')}>
          ⏹ Automatizálás leállítása
        </button>
      )}

      {history.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer" style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>Legutóbbi parancsok</summary>
          <ul className="space-y-1 mt-1" data-testid="command-history">
            {history.map(c => (
              <li key={c.id}>
                <span className={c.status === 'failed' ? 'text-red-300' : ''}>{STATUS_LABELS[c.status]}</span>: {describe(c)}
                {c.resultReason && <> — {REASON_LABELS[c.resultReason] ?? c.resultReason}</>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Sheets */}
      {sheet === 'add' && (
        <ConfirmDialog title="Parancs hozzáadása" confirmLabel="Kész" onCancel={() => setSheet(null)} onConfirm={() => setSheet(null)}>
          <div className="grid grid-cols-3 gap-1.5" role="tablist">
            {(['move', 'survey', 'hold'] as AddTab[]).map(t => (
              <button key={t} type="button" role="tab" aria-selected={addTab === t} className={BTN}
                style={btn(addTab === t ? '#00e5ff' : '#64748b')} onClick={() => setAddTab(t)}>
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
          {addTab === 'move' && systemList(id => void enqueue({ type: 'move', targetSystemId: id }).then(ok => ok && setSheet(null)))}
          {addTab === 'survey' && (
            fleet.capabilities.survey
              ? systemList(id => void enqueue({ type: 'survey', targetSystemId: id }).then(ok => ok && setSheet(null)), s => s.discoveryStatus === 'detected')
              : <p className="text-xs text-slate-400">Felméréshez kutatóhajó kell a flottában.</p>
          )}
          {addTab === 'hold' && (
            <div className="grid grid-cols-2 gap-2">
              {HOLD_OPTIONS.map(sec => (
                <button key={sec} type="button" className={BTN} style={btn()} disabled={busy}
                  onClick={() => void enqueue({ type: 'hold', durationSeconds: sec }).then(ok => ok && setSheet(null))}>
                  {formatDuration(sec * 1000)}
                </button>
              ))}
            </div>
          )}
        </ConfirmDialog>
      )}

      {sheet === 'patrol' && (
        <ConfirmDialog
          title="Járőrútvonal létrehozása"
          confirmLabel="Járőrözés indítása"
          busy={busy}
          onCancel={() => setSheet(null)}
          onConfirm={() => {
            if (route.length < 2) { setError('Válassz legalább 2 rendszert.'); return; }
            void enqueue({ type: 'patrol', route }).then(ok => ok && setSheet(null));
          }}
        >
          <p className="text-xs text-slate-400">Koppints 2–6 ismert rendszerre a sorrendben. A flotta ciklikusan bejárja őket.</p>
          <ol className="flex flex-wrap gap-1.5" aria-label="Kiválasztott útvonal">
            {route.map((id, i) => (
              <li key={`${id}-${i}`} className="flex items-center gap-1 rounded-full pl-3 text-xs"
                style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.4)', color: '#ddd6fe' }}>
                {i + 1}. {systemName(id)}
                <button type="button" aria-label={`${systemName(id)} eltávolítása`} style={{ minWidth: 44, minHeight: 44 }}
                  onClick={() => setRoute(r => r.filter((_, j) => j !== i))}>×</button>
              </li>
            ))}
          </ol>
          {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
          {route.length < 6
            ? systemList(id => setRoute(r => (r[r.length - 1] === id ? r : [...r, id])))
            : <p className="text-xs text-slate-500">Legfeljebb 6 rendszer választható.</p>}
        </ConfirmDialog>
      )}

      {sheet === 'stop' && (
        <ConfirmDialog title="Automatizálás leállítása" confirmLabel="Leállítás" danger busy={busy}
          onCancel={() => setSheet(null)}
          onConfirm={() => void run(() => stopFleetCommands(fleet.id)).then(() => setSheet(null))}>
          <p>Az aktív és a várakozó parancsok törlődnek. A folyamatban lévő ugrás vagy felmérés a szabályok szerint befejeződik.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
